import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Footer from '../components/Footer';
import { resultsPath, homePath, originsHomeUrl } from '../components/franklynet/franklyNetHost';
import { useQuerySuggestions, SuggestionList } from '../components/franklynet/QuerySuggestions';
import { getAskFrankStatus, getAskFrankTrending } from '../services/api';
import { useSiteTheme, resolveThemeAssetUrl } from '../data/siteTheme';

// preview/themeOverride are for the Housekeeping themes desk, which renders this
// page against an unsaved draft. In that mode it must not rename the browser tab
// or pull focus into its search box.
function AskFrank({ preview = false, themeOverride = null }) {
    const [query, setQuery] = useState('');
    const [status, setStatus] = useState({ indexedPages: 0, approvedSources: 0 });
    const [placeholder, setPlaceholder] = useState('Ask Frank anything...');
    const [trending, setTrending] = useState([]);
    const inputRef = useRef(null);
    const navigate = useNavigate();

    const liveTheme = useSiteTheme();
    const theme = themeOverride || liveTheme;
    const { askFrankLogo, askFrankTile } = theme.config;

    useEffect(() => {
        if (!preview) {
            document.title = 'AskFrank';
            inputRef.current?.focus();
        }

        let active = true;

        getAskFrankStatus()
            .then(res => {
                if (!active || !res.success) return;
                setStatus(res);
                const pool = res.placeholders || [];
                if (pool.length > 0) setPlaceholder(pool[Math.floor(Math.random() * pool.length)]);
            })
            .catch(() => {});

        getAskFrankTrending()
            .then(res => { if (active && res.success) setTrending(res.trending || []); })
            .catch(() => {});

        return () => { active = false; };
    }, [preview]);

    const { suggestions, highlight, setHighlight, clear, onKeyDown } = useQuerySuggestions(query);

    const run = (value) => {
        const cleaned = String(value || '').trim();
        if (cleaned.length < 2) {
            inputRef.current?.focus();
            return;
        }
        clear(cleaned);
        navigate(resultsPath(cleaned));
    };

    const submit = (event) => {
        event?.preventDefault();
        run(query);
    };

    const pick = (item) => {
        setQuery(item);
        run(item);
    };

    return (
        <div
            className="min-h-screen w-full flex flex-col bg-repeat"
            style={{
                backgroundImage: `url(${resolveThemeAssetUrl(askFrankTile.url)})`,
                imageRendering: 'pixelated',
            }}
        >
            <div className="flex-1 flex flex-col items-center justify-center px-4 py-16">
                <div className="w-full max-w-[680px]">
                    <img
                        src={resolveThemeAssetUrl(askFrankLogo.url)}
                        alt="AskFrank"
                        draggable={false}
                        className="relative mb-4 ml-3 cursor-pointer"
                        onClick={() => navigate(homePath())}
                        style={{
                            left: askFrankLogo.offsetX,
                            top: askFrankLogo.offsetY,
                            width: 'auto',
                            height: 'auto',
                            maxWidth: 'none',
                            maxHeight: 'none',
                            objectFit: 'none',
                            flexShrink: 0,
                            imageRendering: 'pixelated',
                        }}
                    />

                    <form onSubmit={submit} className="w-full">
                        <div className="flex w-full items-stretch gap-2 bg-offwhite border border-gray-400 p-2 shadow-[0_3px_0_0_rgba(0,0,0,0.5)]">
                            <div className="relative min-w-0 flex-1">
                                <input
                                    ref={inputRef}
                                    type="text"
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    onKeyDown={(e) => onKeyDown(e, pick)}
                                    onBlur={clear}
                                    autoComplete="off"
                                    maxLength={300}
                                    placeholder={placeholder}
                                    aria-label="Ask Frank a question"
                                    className="w-full bg-white border border-gray-300 px-3 py-2 text-[16px] text-gray-800 outline-none focus:border-maroon"
                                />
                                <SuggestionList
                                    suggestions={suggestions}
                                    highlight={highlight}
                                    onPick={pick}
                                    onHighlight={setHighlight}
                                />
                            </div>

                            <button
                                type="submit"
                                className="shrink-0 bg-maroon text-white font-['Jersey_10'] uppercase text-[20px] tracking-[2px] px-6 border border-black/30 transition-transform hover:-translate-y-[1px] active:translate-y-[1px]"
                                style={{ boxShadow: '0 2px 0 0 rgba(0,0,0,0.4)' }}
                            >
                                AskFrank!
                            </button>
                        </div>
                    </form>
                </div>

                {trending.length > 0 && (
                    <div className="mt-8 w-full max-w-[680px] text-center">
                        <p className="font-['Jersey_10'] uppercase text-white/80 text-[15px] tracking-[1px] mb-2">
                            Others have asked
                        </p>
                        <div className="flex flex-wrap justify-center gap-2">
                            {trending.slice(0, 5).map(item => (
                                <button
                                    key={item.query}
                                    type="button"
                                    onClick={() => navigate(resultsPath(item.query))}
                                    className="bg-black/25 hover:bg-black/40 text-white text-[13px] px-3 py-1 rounded-full transition-colors"
                                >
                                    {item.query}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                <div className="mt-10 text-center">
                    <a
                        href={originsHomeUrl()}
                        className="font-['Jersey_10'] uppercase text-white text-[18px] tracking-[1px] underline underline-offset-4 hover:text-gold transition-colors"
                        style={{ textShadow: '-1px 0 0 #000, 1px 0 0 #000, 0 -1px 0 #000, 0 1px 0 #000' }}
                    >
                        Return to Frankly Origins
                    </a>

                    {status.indexedPages > 0 && (
                        <p className="mt-3 text-white/60 text-[12px]">
                            Frank has read {status.indexedPages.toLocaleString()} pages of Origins information!
                        </p>
                    )}
                </div>
            </div>

            <div className="bg-offwhite/95 pt-4">
                <Footer />
            </div>
        </div>
    );
}

export default AskFrank;
